/** UTF-8 byte length of a JSON-serialized value. */
export function jsonByteLength(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

/**
 * Greedy fill: take events until the next one would overflow `maxBytes` or
 * `maxEvents`. An event that never fits alone is dropped; everything after the
 * first overflow stays queued.
 */
export function selectChunk<T>(
  queue: readonly T[],
  limits: { maxBytes: number; maxEvents: number },
): { selected: T[]; remaining: T[] } {
  const selected: T[] = [];
  let index = 0;

  while (index < queue.length && selected.length < limits.maxEvents) {
    const event = queue[index] as T;
    const candidate = [...selected, event];
    if (jsonByteLength(candidate) > limits.maxBytes) {
      if (selected.length === 0) {
        index += 1;
        continue;
      }
      break;
    }
    selected.push(event);
    index += 1;
  }

  return { selected, remaining: queue.slice(index) as T[] };
}

/** Network (no status), 429 and 5xx retry; other 4xx do not. */
export function shouldRetry(status?: number): boolean {
  if (status === undefined || status === 0) return true;
  if (status === 429) return true;
  if (status >= 500) return true;
  return false;
}

const BACKOFF_MS = [1_000, 2_000, 4_000] as const;

/**
 * Delay before attempt `attempt` (0-based retry count). Prefers `Retry-After`
 * seconds when present and ≤ 30; otherwise 1 s, 2 s, 4 s.
 */
export function retryDelayMs(attempt: number, retryAfterSeconds?: number): number {
  if (
    retryAfterSeconds !== undefined &&
    Number.isFinite(retryAfterSeconds) &&
    retryAfterSeconds > 0 &&
    retryAfterSeconds <= 30
  ) {
    return Math.round(retryAfterSeconds * 1_000);
  }
  return BACKOFF_MS[Math.min(Math.max(attempt, 0), BACKOFF_MS.length - 1)] ?? 4_000;
}

/** Parse a `Retry-After` header value (seconds only; HTTP-date is ignored). */
export function parseRetryAfterSeconds(header: string | null | undefined): number | undefined {
  if (!header) return undefined;
  const seconds = Number(header.trim());
  if (!Number.isFinite(seconds) || seconds < 0) return undefined;
  return seconds;
}

export const MAX_CHUNK_RETRIES = 3;
